'use client';

import * as React from 'react';
import Image from 'next/image';
import { Menu, XIcon } from 'lucide-react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/atoms/primitives/dialog';
import { GlassControl } from '@/components/atoms/GlassControl';
import { cn } from '@/components/atoms/primitives/utils';
import {
  LociDotsMark,
  LociLettermarkMask,
  isNationalLettermarkSrc,
} from '@/components/atoms/LociLettermark';
import { GlobalNavCoordStrip } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import { useAnimationFrameCallback } from '@/hooks/useAnimationFrameCallback';

/**
 * NavMenuSheet — the **surface-neutral** full-height global-nav menu shell
 * (WEB-613 → WEB-629, per the DES-60 rescope).
 *
 * One shell, surface-aware content. Both the marketing-city surface
 * (`CityNavMenu`) and the authed app surface (`AppNavMenu`) mount this same
 * component, so the parts that are expensive to get right and dangerous to
 * duplicate — focus trap, focus return, scroll lock, Escape, the takeover
 * geometry and entry motion — exist exactly once.
 *
 * ## The seam
 *
 * | The shell owns                                | The host supplies                           |
 * | --------------------------------------------- | ------------------------------------------- |
 * | Overlay + full-viewport takeover geometry     | `open` / `onOpenChange` (it owns state)     |
 * | Focus trap, focus return, Escape, scroll lock | The trigger ({@link NavMenuTrigger})        |
 * | Coord-strip + chrome-row + body layout        | `eyebrow`, `chromeStart`, `children`        |
 * | The 44px close affordance                     | `accentVars`, `title`, `description`        |
 * | The ≥721px dropdown geometry                  | `wideLayout` (takeover or dropdown)         |
 *
 * Deliberately absent: any `CityCode`, `cityUrl()` call, city copy, or
 * assumption about which nav items exist. A surface that needs those resolves
 * them itself and passes the result down — see `CityNavMenu` / `AppNavMenu`.
 *
 * Renders at every width; do not gate behind `lg:hidden`.
 *
 * ## `wideLayout="dropdown"` (FRE-1390, DS 1.9.1 `.o-globalnav__menu`)
 *
 * The avatar's account drawer is the 240px top-right dropdown ≥721px and the
 * full takeover ≤720px. It is the same `Dialog`, so the focus trap, Escape,
 * focus return and scroll lock do not fork by width. Only the geometry does,
 * and that is CSS (`styles/overrides/chrome.css`): ≥721px it hides the
 * takeover-only coord strip and chrome row, close button included. The DS
 * dropdown has neither, and Escape or a click outside closes it. The one JS
 * consequence: focus goes to the first row the visitor can see (see
 * `focusFirstRendered`).
 */

const OVERLAY_CLASSNAME = cn(
  // WEB-689: theme-aware overlay. Light is indigo-tinted + 8px blur (dark
  // glass blur reads as fog on paper). Dark stays rgba(15,20,31,0.85) / 20px.
  'bg-nav-sheet-overlay backdrop-blur-nav-sheet',
  // Ride `--z-modal` (100) so the takeover paints above the dock (`--z-dock`: 90).
  'z-modal',
);

const CONTENT_CLASSNAME = cn(
  'z-modal',
  // Full-viewport takeover at every width — reset the dialog atom's centered
  // card AND its `sm:max-w-lg` (twMerge won't drop a breakpoint variant for a
  // base `max-w-*`, so we must restate `sm:max-w-none` explicitly).
  'top-0 left-0 translate-x-0 translate-y-0',
  'h-[100dvh] max-h-[100dvh] w-screen max-w-none sm:max-w-none',
  'rounded-none border-0 p-0 shadow-none gap-0',
  // DS `gn-sheet-in` (220ms / cubic-bezier(0.2,0.8,0.2,1) via token). Reduced
  // motion is neutralized centrally in styles/overrides/reduced-motion.css.
  'animate-loci-sheet-in',
  // Neutralize the dialog atom's zoom entrance (inert without the animate
  // plugin, but keep classes honest for any future plugin install).
  'data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100',
  // Hide DialogContent's baked-in absolute Close — the chrome owns a 44px close.
  '[&>button]:hidden',
  'flex flex-col overflow-hidden nav-menu-sheet-surface text-fg-1',
);

/**
 * Call-site skin for the nav's glass icon controls (menu trigger, sheet
 * close), rendered by the `GlassControl` atom at the nav size (WEB-1021): a
 * 36px circle (DS 1.9.0 `.o-globalnav__icon-btn`) whose fill / ring / glow on
 * hover and open (`aria-expanded`) and whose coarse-pointer 44 `::before` hit
 * floor all come from the atom. On a fine pointer the hit box is the paint.
 */
const GN_ICON_CONTROL = 'shrink-0 text-fg-2';

/** Menu trigger: the glyph steps to the layer glow on hover and while open. */
const TRIGGER_CONTROL_CLASSNAME = cn(GN_ICON_CONTROL, 'hover:text-[color:var(--gn-accent-glow)]');

/**
 * Open-state glyph, applied from the `isOpen` prop rather than a
 * `data-[state=open]:` variant: measured in-browser, that variant family did
 * not compile in this Tailwind setup while `hover:` on the same element did.
 * The open fill / ring / glow is the atom's `[aria-expanded='true']` state.
 */
const TRIGGER_OPEN_CLASSNAME = 'text-[color:var(--gn-accent-glow)]';

const CLOSE_CONTROL_CLASSNAME = cn(GN_ICON_CONTROL, 'hover:text-fg-1');

/** Coord strip + chrome row: hidden in the ≥721px dropdown, inert alone. */
const TAKEOVER_CHROME_CLASSNAME = 'nav-menu-sheet__takeover-chrome';

export type NavMenuSheetWideLayout = 'takeover' | 'dropdown';

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Focus the first tabbable in `root` that renders a box (skips `display: none`).
 * Returns false when there is none (jsdom renders no boxes), so the caller can
 * leave focus to Radix.
 */
function focusFirstRendered(root: HTMLElement): boolean {
  const first = Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).find(
    (element) => element.getClientRects().length > 0,
  );
  first?.focus();
  return first !== undefined;
}

export interface NavMenuTriggerProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-expanded'
> {
  /** Drives `aria-expanded` and the `data-state` open styling. */
  isOpen: boolean;
  /** Accessible name. Defaults to "Open menu". */
  label?: string;
}

/**
 * The menu trigger, owned and mounted by the **host** header — `CityNavMenu`
 * on the city surface, `AppNavMenu` on the app surface (DES-60). It ships from
 * this module only so both hosts inherit identical geometry (36px visual /
 * 44px hit area) and dialog semantics; the shell itself never renders it.
 */
export const NavMenuTrigger = React.forwardRef<HTMLButtonElement, NavMenuTriggerProps>(
  function NavMenuTrigger(
    { isOpen, label = 'Open menu', className, ...rest }: NavMenuTriggerProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ): React.ReactElement {
    return (
      <GlassControl
        {...rest}
        ref={ref}
        size="nav"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-state={isOpen ? 'open' : 'closed'}
        className={cn(TRIGGER_CONTROL_CLASSNAME, isOpen && TRIGGER_OPEN_CLASSNAME, className)}
      >
        <Menu aria-hidden="true" className="size-4" strokeWidth={2} />
      </GlassControl>
    );
  },
);

export interface NavMenuLockupProps {
  /** Lockup image source — the host picks its brand asset. */
  src: string;
  /** Alt text for the lockup image. */
  alt: string;
  /** Decorative label rendered beside the lockup, e.g. a city or layer name. */
  label: string;
}

/**
 * The chrome-row lockup pair (image + label) both hosts hand to
 * {@link NavMenuSheetProps.chromeStart}. Surface-neutral: it names nothing
 * itself and takes the asset, alt text, and label from its host, so the two
 * surfaces cannot drift on this chrome without also duplicating it.
 *
 * FRE-1361 / DES-85 (2026-09-27): the national branch now matches the
 * city-header bar lockup — dot-cluster symbol + masked lettermark (themed
 * via `--nav-lettermark`: ink on light, cream on dark) — instead of the
 * bare amber `LociLettermark`. Both `CityNavMenu` and `AppNavMenu` mount
 * this component, so both menus (and both `AppNavMenu` themes) pick up the
 * new lockup automatically.
 *
 * FRE-1388: no vertical rule between the mark and the label, at any width —
 * same operator ruling as the city-header bar. The `·` is the only separator.
 */
export function NavMenuLockup({ src, alt, label }: NavMenuLockupProps): React.ReactElement {
  return (
    <>
      {isNationalLettermarkSrc(src) ? (
        <>
          <LociDotsMark data-testid="nav-menu-lockup-dots" />
          <LociLettermarkMask alt={alt} data-testid="nav-menu-lockup-lettermark" />
        </>
      ) : (
        <Image src={src} alt={alt} width={64} height={20} className="h-5 w-auto" />
      )}
      <span
        aria-hidden="true"
        data-testid="nav-menu-sheet-lockup-label"
        className="inline-flex items-center whitespace-nowrap font-mono text-micro uppercase tracking-[0.14em] text-[color:var(--gn-accent-glow)]"
      >
        · {label}
      </span>
    </>
  );
}

export interface NavMenuSheetProps {
  /** Accessible name for the takeover (rendered sr-only). */
  title: string;
  /** Accessible description for the takeover (rendered sr-only). */
  description: string;
  /**
   * Coord-strip eyebrow, e.g. `CITY · CLT.LOCI.CITY`. Host-resolved — the
   * shell never derives a host, city, or layer of its own.
   */
  eyebrow: string;
  /**
   * Accent custom properties for the takeover, from `GLOBAL_NAV_ACCENT_VARS`.
   * The host picks its layer; the shell applies what it is handed.
   */
  accentVars?: React.CSSProperties;
  /** Leading chrome-row slot — the host's lockup / wordmark / label. */
  chromeStart?: React.ReactNode;
  /** Menu body. */
  children?: React.ReactNode;
  /**
   * Ref to the host's trigger button. **Required for focus return** — without
   * a `DialogTrigger` inside the shell, Radix has no element to restore focus
   * to on close, so it would land on `<body>`. The host owns the trigger
   * (DES-60), so the host hands its ref down.
   */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** Controlled open state — the host owns it alongside its trigger. */
  open?: boolean;
  /** Uncontrolled initial open — Storybook / tests only. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Accessible name for the close button. Defaults to "Close menu". */
  closeLabel?: string;
  /**
   * When false, close still dismisses the dialog but does **not** move focus
   * back to the host trigger. Used when another overlay (e.g. SearchSheet)
   * is claiming focus as part of the same user gesture (WEB-614).
   * Defaults to true.
   */
  restoreFocusOnClose?: boolean;
  /**
   * ≥721px geometry: `takeover` (default) or `dropdown`, the DS 240px avatar
   * menu (FRE-1390). ≤720px both are the takeover.
   */
  wideLayout?: NavMenuSheetWideLayout;
  /** Extra classes on the takeover surface. */
  className?: string;
}

export function NavMenuSheet({
  title,
  description,
  eyebrow,
  accentVars,
  chromeStart,
  children,
  triggerRef,
  open,
  defaultOpen = false,
  onOpenChange,
  closeLabel = 'Close menu',
  restoreFocusOnClose = true,
  wideLayout = 'takeover',
  className,
}: NavMenuSheetProps): React.ReactElement {
  const titleId = React.useId();
  const isDropdown = wideLayout === 'dropdown';
  const scheduleFrame = useAnimationFrameCallback();

  // Whatever held focus when the sheet opened. Used only as a backstop for a
  // host that forgot `triggerRef` — see `handleCloseAutoFocus`. Captured in
  // `onOpenAutoFocus` rather than `onOpenChange`, because `onOpenChange` does
  // not fire when the HOST flips its own `open` state; this event does, and it
  // runs before Radix moves focus into the content, so `document.activeElement`
  // is still the opener.
  const openerRef = React.useRef<HTMLElement | null>(null);

  // The dropdown picks focus itself, on open and after a 720/721 crossing.
  // Radix skips links when it chooses, so ≥721px, with the close button
  // `display: none`, it would land on the first BUTTON row (Notifications,
  // four rows down). Instead focus goes to the first rendered tabbable, which
  // is the close button ≤720px and the first row ≥721px.
  //
  // Crossing to ≥721px while open hides a focused close button, and Chromium
  // blurs it to `<body>` (`relatedTarget: null`). Radix's trap ignores that
  // blur, and its observer only catches removed nodes, not hidden ones. So the
  // next frame re-homes focus. Focus that left for a real target, a window
  // blur (the element stays active) and the dialog unmounting are left alone.
  const handleOpenAutoFocus = React.useCallback(
    (event: Event) => {
      openerRef.current = document.activeElement as HTMLElement | null;
      if (!isDropdown || !(event.target instanceof HTMLElement)) return;
      if (focusFirstRendered(event.target)) event.preventDefault();
    },
    [isDropdown],
  );

  const handleDropdownFocusOut = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (!isDropdown || event.relatedTarget !== null) return;
      const content = event.currentTarget;
      scheduleFrame(() => {
        const active = document.activeElement;
        if (!content.isConnected || (active && active !== document.body)) return;
        focusFirstRendered(content);
      });
    },
    [isDropdown, scheduleFrame],
  );

  // Radix's own `onCloseAutoFocus` restores focus to the trigger registered by
  // `DialogTrigger` — which this shell deliberately does not render, since the
  // host owns the trigger (DES-60). So we claim the event and focus the host's
  // ref ourselves.
  //
  // Note there is no "let Radix handle it" fallback available: Radix composes
  // its own handler after ours and calls `preventDefault()` unconditionally
  // before focusing `context.triggerRef.current`, which is null without a
  // `DialogTrigger`. Returning early therefore drops focus to `<body>` rather
  // than restoring it — so we fall back to the opener we recorded instead.
  // Focus is only genuinely abandoned for trigger-less `defaultOpen` mounts
  // (stories, tests), where no opener ever existed to return to.
  //
  // `restoreFocusOnClose={false}` still preventDefaults (so Radix cannot land
  // on `<body>`) but skips focusing the trigger — another overlay opened in
  // the same gesture (SearchSheet from the city menu) owns focus instead.
  const handleCloseAutoFocus = React.useCallback(
    (event: Event) => {
      const target = triggerRef?.current ?? openerRef.current;
      // Release the captured opener either way — holding a detached node would
      // keep its whole subtree reachable for the lifetime of this island.
      openerRef.current = null;
      event.preventDefault();
      if (!restoreFocusOnClose) return;
      if (!target?.isConnected) return;
      target.focus();
    },
    [triggerRef, restoreFocusOnClose],
  );

  return (
    <Dialog defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="nav-menu-sheet"
        aria-labelledby={titleId}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        onBlur={handleDropdownFocusOut}
        style={accentVars}
        className={cn(CONTENT_CLASSNAME, isDropdown && 'nav-menu-sheet--dropdown', className)}
        overlayClassName={cn(OVERLAY_CLASSNAME, isDropdown && 'nav-menu-sheet-overlay--dropdown')}
      >
        <DialogTitle id={titleId} className="sr-only">
          {title}
        </DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>

        <GlobalNavCoordStrip
          eyebrow={eyebrow}
          testId="nav-menu-sheet-coord"
          className={cn('shrink-0 border-border-strong bg-bg-sunken', TAKEOVER_CHROME_CLASSNAME)}
        />

        <div
          data-testid="nav-menu-sheet-chrome"
          className={cn(
            'flex shrink-0 items-center justify-between gap-3 border-b border-nav-strip-border px-3.5 py-3 md:px-5',
            TAKEOVER_CHROME_CLASSNAME,
          )}
        >
          <div className="flex min-w-0 items-center gap-1.5">{chromeStart}</div>

          <DialogClose asChild>
            <GlassControl
              size="nav"
              aria-label={closeLabel}
              data-testid="nav-menu-sheet-close"
              className={CLOSE_CONTROL_CLASSNAME}
            >
              <XIcon aria-hidden="true" className="size-4" strokeWidth={2} />
            </GlassControl>
          </DialogClose>
        </div>

        <div
          data-testid="nav-menu-sheet-body"
          className="nav-menu-sheet__body flex min-h-0 flex-1 flex-col overflow-y-auto"
        >
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
